import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { useCurrentInfluencerProfile } from '../../hooks/useCurrentInfluencerProfile';
import { subscribeFeedbackComments, addFeedbackComment } from '../../lib/feedbackPosts/firestore';
import { validateCommentBody } from '../../lib/feedbackPosts/validation';
import { createFeedbackCommentNotification } from '../../lib/notifications/firestore';
import FeedbackCommentItem from './FeedbackCommentItem';

const COMMENT_PREVIEW_COUNT = 3;

const FEEDBACK_COMMENT_EMAIL_API = '/api/email/feedback-comment-notification';

export default function FeedbackPostExpandedContent({
  postId,
  isOpen,
  onNavigate,
  showAllComments,
  onToggleShowAllComments,
}) {
  const { user, profile } = useAuth();
  const { influencerProfile } = useCurrentInfluencerProfile();
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentError, setCommentError] = useState(null);
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    const unsub = subscribeFeedbackComments(postId, setComments, () => {});
    return () => unsub();
  }, [postId]);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) {
      setCommentError('Sign in to comment.');
      return;
    }
    if (submittingComment) return;
    const v = validateCommentBody(commentText);
    if (!v.ok) {
      setCommentError(v.message);
      return;
    }
    setCommentError(null);
    setSubmittingComment(true);
    try {
      const result = await addFeedbackComment({
        postId,
        authorId: user.uid,
        body: v.body,
      });
      setCommentText('');
      const recipientUserId = String(result.postAuthorId ?? '').trim();
      if (recipientUserId && recipientUserId !== user.uid) {
        const displayName =
          influencerProfile?.displayName ||
          influencerProfile?.identity?.displayName ||
          profile?.displayName ||
          'Creator';
        const username =
          influencerProfile?.username ||
          influencerProfile?.identity?.handle ||
          profile?.username ||
          '';
        const photoURL =
          influencerProfile?.photoURL ||
          influencerProfile?.profileImageUrl ||
          profile?.photoURL ||
          null;
        try {
          const notificationId = await createFeedbackCommentNotification({
            recipientUserId,
            actorUserId: user.uid,
            actorDisplayName: displayName,
            actorUsername: username,
            actorPhotoURL: photoURL,
            postId,
            commentId: result.commentId,
            postTitle: result.postTitle,
            previewText: v.body,
          });
          console.log('[FeedbackPostExpandedContent] notification created', {
            notificationId,
            recipientUserId,
            postId,
            commentId: result.commentId,
          });
        } catch (notifErr) {
          console.warn('[FeedbackPostExpandedContent] notification create failed', notifErr);
        }
        try {
          const res = await fetch(FEEDBACK_COMMENT_EMAIL_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              recipientUserId,
              actorDisplayName: displayName,
              postId,
              postTitle: result.postTitle,
              commentPreview: v.body,
            }),
          });
          if (!res.ok) {
            const txt = await res.text().catch(() => '');
            let errorJson = null;
            try {
              errorJson = JSON.parse(txt);
            } catch {
              /* not JSON */
            }
            console.warn('[FeedbackPostExpandedContent] email API failed', {
              status: res.status,
              error: errorJson?.error || null,
              txt: txt.slice(0, 400),
              recipientUserId,
              postId,
            });
          }
        } catch (emailErr) {
          console.warn('[FeedbackPostExpandedContent] notification email failed', emailErr);
        }
      }
      if (!recipientUserId) {
        console.warn('[FeedbackPostExpandedContent] missing postAuthorId; skipping notifications/email', {
          postId,
          commentId: result.commentId,
          postTitle: result.postTitle,
        });
      }
    } catch (err) {
      setCommentError(err?.message || 'Could not post comment.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const displayComments = showAllComments ? comments : comments.slice(0, COMMENT_PREVIEW_COUNT);
  const hasMoreComments = comments.length > COMMENT_PREVIEW_COUNT;

  return (
    <div
      className="border-t border-slate-200 bg-slate-50/50"
      onClick={(e) => e.stopPropagation()}
      role="region"
      aria-label="Post discussion"
    >
      <div className="px-3 py-4 sm:px-5 sm:py-4">
        {/* Discussion section only */}
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Discussion</h3>

          {isOpen && user && (
            <form onSubmit={handleComment} className="mt-3">
              {commentError && (
                <p className="mb-2 text-sm text-red-600" role="alert">
                  {commentError}
                </p>
              )}
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                rows={3}
                placeholder="Share constructive, specific feedback."
                disabled={submittingComment}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-ihTeal focus:ring-ihTeal disabled:bg-slate-50"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                type="submit"
                disabled={submittingComment}
                className="mt-2 rounded-lg bg-ihTeal px-3 py-2 text-sm font-semibold text-white hover:bg-ihTeal-dark disabled:opacity-60"
              >
                {submittingComment ? 'Posting…' : 'Post comment'}
              </button>
            </form>
          )}

          {isOpen && !user && (
            <p className="mt-3 text-sm text-slate-500">
              Sign in to leave a comment.
            </p>
          )}

          {!isOpen && (
            <p className="mt-3 text-sm text-slate-500">
              This post is closed. New comments are disabled.
            </p>
          )}

          <ul className="mt-4 space-y-3">
            {comments.length === 0 && (
              <li className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
                No comments yet. Be the first to help out.
              </li>
            )}
            {displayComments.map((c) => (
              <FeedbackCommentItem
                key={c.id}
                comment={c}
                postId={postId}
                currentUserId={user?.uid ?? null}
                onNavigate={onNavigate}
                variant="compact"
              />
            ))}
          </ul>

          {hasMoreComments && (
            <div className="mt-3">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleShowAllComments();
                }}
                className="text-sm font-medium text-ihTeal hover:text-ihTeal-dark focus:outline-none focus:underline"
              >
                {showAllComments
                  ? 'Show less'
                  : `Show all ${comments.length} comments`}
              </button>
            </div>
          )}
        </div>

        {/* Open full page */}
        <div className="mt-4 pt-3 border-t border-slate-200">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate('feedback-detail', postId);
            }}
            className="text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            Open full page
          </button>
        </div>
      </div>
    </div>
  );
}
